import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const ImageUpload = ({
  images = [],
  onImagesSelected,
  onDeleteImage,
  onSetMainImage,
  isUploading = false,
  maxImages = 3,
  disabled = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    validateAndAddFiles(files);
  };

  const validateAndAddFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type. Only JPEG, PNG, WebP, and GIF are allowed.`);
        return;
      }

      // Check file size (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size exceeds 5MB limit.`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setValidationError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      onImagesSelected(validFiles);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    validateAndAddFiles(files);
  };

  const canAddMore = images.length < maxImages;

  return (
    <Box>
      {/* Validation Error */}
      {validationError && (
        <Alert severity="warning" onClose={() => setValidationError('')} sx={{ mb: 1 }}>{validationError}</Alert>
      )}

      {/* Upload Area */}
      {canAddMore && (
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            padding: 3,
            textAlign: 'center',
            backgroundColor: dragActive ? 'action.hover' : 'background.paper',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            mb: 2,
          }}
        >
          <input
            accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: 'none' }}
            id="image-upload-input"
            multiple
            type="file"
            onChange={handleFileSelect}
            disabled={disabled}
          />
          <label htmlFor="image-upload-input">
            <Stack spacing={1} alignItems="center">
              <PhotoCamera sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="body1" color="textPrimary">
                Drag and drop images here
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to browse
              </Typography>
              <Typography variant="caption" color="textSecondary">
                JPEG, PNG, WebP, GIF • Max 5MB per file • Up to {maxImages} images
              </Typography>
              <Button
                variant="contained"
                component="span"
                startIcon={<PhotoCamera />}
                disabled={disabled}
              >
                Select Images
              </Button>
            </Stack>
          </label>
        </Box>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            Uploading images...
          </Typography>
        </Box>
      )}

      {/* Images Display */}
      {images.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Images ({images.length}/{maxImages})
          </Typography>
          <ImageList
            sx={{ width: '100%', maxHeight: 450 }}
            cols={3}
            rowHeight={164}
            gap={8}
          >
            {images.map((image, index) => {
              const isMainImage = image.isMainImage || false;
              const imageUrl = image.url || (image instanceof File ? URL.createObjectURL(image) : '');

              return (
                <ImageListItem key={image.storagePath || image.name || index}>
                  <img
                    src={imageUrl}
                    alt={image.name || `Image ${index + 1}`}
                    loading="lazy"
                    style={{
                      height: '164px',
                      objectFit: 'cover',
                      cursor: onSetMainImage ? 'pointer' : 'default',
                    }}
                    onClick={() => onSetMainImage && !isMainImage && onSetMainImage(image)}
                  />
                  <ImageListItemBar
                    sx={{
                      background:
                        'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                    }}
                    position="top"
                    actionIcon={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {onSetMainImage && (
                          <IconButton
                            sx={{ color: isMainImage ? 'warning.main' : 'rgba(255, 255, 255, 0.7)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isMainImage) onSetMainImage(image);
                            }}
                          >
                            {isMainImage ? <StarIcon /> : <StarBorderIcon />}
                          </IconButton>
                        )}
                        {onDeleteImage && (
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteImage(image);
                            }}
                            disabled={disabled}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    }
                    actionPosition="right"
                  />
                  {isMainImage && (
                    <ImageListItemBar
                      title="Main Image"
                      position="below"
                    />
                  )}
                </ImageListItem>
              );
            })}
          </ImageList>
        </Box>
      )}

      {/* Max Images Warning */}
      {images.length >= maxImages && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Maximum number of images ({maxImages}) reached. Delete images to add more.
        </Alert>
      )}
    </Box>
  );
};

export default ImageUpload;
